import React, { ReactNode } from 'react'

/**
 * A verbatim that refers to a tested item by the label respondents saw ("Ad 2")
 * travels with typed runs, stamped by Python (`quote_text_runs`): their texts
 * join to exactly the verbatim, and a run with a `gloss` is drawn followed by
 * " [gloss]", the item's name ("Ad 2 [Sausage biscuit] was funnier"). The
 * words inside the quotation marks stay the respondent's; the gloss is muted.
 * Nothing here locates a span or reads an offset: the runs say where.
 */
export interface QuoteTextRunLike {
  text: string
  gloss?: string | null
}

type QuoteRuns = ReadonlyArray<QuoteTextRunLike> | null | undefined

/**
 * The string a reader sees: the verbatim, each glossed run followed by
 * " [gloss]". Python's `rendered_quote_text` prints the same string, and the
 * fit gate measures it.
 */
export function glossedQuoteText(text: string, runs?: QuoteRuns): string {
  if (!runs?.length) return text
  return runs.map((run) => (run.gloss ? `${run.text} [${run.gloss}]` : run.text)).join('')
}

/**
 * The verbatim drawn from its runs, each gloss in a muted span; the plain text
 * when it has none. Every surface that draws a quote with runs calls this.
 */
export function glossedQuote(text: string, runs?: QuoteRuns): ReactNode {
  if (!runs?.length) return text
  return runs.map((run, index) => (
    <React.Fragment key={index}>
      {run.text}
      {run.gloss ? (
        <span data-quote-gloss="" style={{ opacity: 0.6 }}>
          {` [${run.gloss}]`}
        </span>
      ) : null}
    </React.Fragment>
  ))
}
